import { useLocationService } from '@grafana/runtime';
import { ChatContextItem } from './types';
import { useEffect, useState, useRef, useCallback } from 'react';

// Extend window interface for global registry
declare global {
  interface Window {
    [key: string]: any;
  }
}

export interface PageContextRegistration {
  id: string;
  urlPattern: string | RegExp;
  context: ChatContextItem[];
}

// Global registry key
const GLOBAL_REGISTRY_KEY = '__grafanaAssistantPageContextRegistry__';

// Custom event for registry changes
const REGISTRY_CHANGE_EVENT = 'grafana-assistant:page-context-registry-changed';

/**
 * Legacy event names for backward compatibility with the previous implementation.
 * These events are emitted alongside the new REGISTRY_CHANGE_EVENT to ensure
 * any existing code listening to the old events continues to work.
 *
 * @deprecated These event name is maintained for backward compatibility only.
 * TODO: Remove after old event-based approach is fully deprecated
 */
const LEGACY_REGISTRY_UPDATE_EVENT = 'grafana-assistant:page-context-update';
/**
 * @deprecated These event name is maintained for backward compatibility only.
 * TODO: Remove after old event-based approach is fully deprecated
 */
const LEGACY_REGISTRY_REMOVE_EVENT = 'grafana-assistant:page-context-remove';
/**
 * @deprecated These event name is maintained for backward compatibility only.
 * TODO: Remove after old event-based approach is fully deprecated
 */
const LEGACY_REGISTRY_SYNC_EVENT = 'grafana-assistant:page-context-sync';

// Ensure global registry exists on window
if (!window[GLOBAL_REGISTRY_KEY]) {
  window[GLOBAL_REGISTRY_KEY] = [];
}

// Get reference to the global registry
function getPageContextRegistry(): PageContextRegistration[] {
  return window[GLOBAL_REGISTRY_KEY];
}

// TODO: Remove skipLegacyEvents parameter after old event-based approach is fully deprecated
function setPageContextRegistry(newRegistry: PageContextRegistration[], skipLegacyEvents = false): void {
  window[GLOBAL_REGISTRY_KEY] = newRegistry;
  // Notify all listeners that the registry has changed
  window.dispatchEvent(new CustomEvent(REGISTRY_CHANGE_EVENT));

  // TODO: Remove this legacy event emission after old event-based approach is fully deprecated
  // Emit legacy sync event for backward compatibility (unless we're processing a legacy event)
  if (!skipLegacyEvents) {
    window.dispatchEvent(
      new CustomEvent(LEGACY_REGISTRY_SYNC_EVENT, {
        detail: { registry: newRegistry },
      })
    );
  }
}

/**
 * Bridge to handle legacy events from external apps using old versions of @grafana/assistant.
 * This ensures that registrations from external apps get synced into our window registry.
 *
 * TODO: Remove this entire bridge after old event-based approach is fully deprecated
 */
let legacyBridgeInitialized = false;

function initializeLegacyEventBridge(): void {
  if (legacyBridgeInitialized) {
    return;
  }
  legacyBridgeInitialized = true;

  // Handle legacy update events (registration added or updated)
  window.addEventListener(LEGACY_REGISTRY_UPDATE_EVENT, (event: Event) => {
    const customEvent = event as CustomEvent;
    const registration = customEvent.detail as PageContextRegistration;

    if (!registration || !registration.id) {
      return;
    }

    const currentRegistry = getPageContextRegistry();
    const existingIndex = currentRegistry.findIndex((r) => r.id === registration.id);

    if (existingIndex !== -1) {
      // Update existing registration
      const newRegistry = [...currentRegistry];
      newRegistry[existingIndex] = registration;
      setPageContextRegistry(newRegistry, true); // Skip legacy events to avoid loops
    } else {
      // Add new registration
      setPageContextRegistry([...currentRegistry, registration], true); // Skip legacy events to avoid loops
    }
  });

  // Handle legacy remove events (registration removed)
  window.addEventListener(LEGACY_REGISTRY_REMOVE_EVENT, (event: Event) => {
    const customEvent = event as CustomEvent;
    const id = customEvent.detail?.id;

    if (!id) {
      return;
    }

    const currentRegistry = getPageContextRegistry();
    const index = currentRegistry.findIndex((r) => r.id === id);

    if (index !== -1) {
      const newRegistry = currentRegistry.filter((_, i) => i !== index);
      setPageContextRegistry(newRegistry, true); // Skip legacy events to avoid loops
    }
  });

  // Handle legacy sync events (entire registry sync from external app)
  window.addEventListener(LEGACY_REGISTRY_SYNC_EVENT, (event: Event) => {
    const customEvent = event as CustomEvent;
    const incomingRegistry = customEvent.detail?.registry as PageContextRegistration[];

    if (!Array.isArray(incomingRegistry)) {
      return;
    }

    const currentRegistry = getPageContextRegistry();
    const currentIds = new Set(currentRegistry.map((r) => r.id));

    // Merge: add any registrations from external app that we don't have
    const newRegistrations = incomingRegistry.filter((r) => !currentIds.has(r.id));

    if (newRegistrations.length > 0) {
      setPageContextRegistry([...currentRegistry, ...newRegistrations], true); // Skip legacy events to avoid loops
    }
  });
}

// TODO: Remove this initialization after old event-based approach is fully deprecated
// Initialize the bridge on module load
initializeLegacyEventBridge();

/**
 * Registers context items for specific pages based on URL patterns.
 * Returns a setter function to update the context dynamically, similar to useState.
 *
 * Multiple registrations with the same URL pattern are allowed and will all be merged
 * when the pattern matches. Each call creates a new independent registration.
 *
 * @param urlPattern - URL pattern (string or RegExp) to match against page URLs
 * @param initialContext - Initial array of ChatContextItem to provide when the pattern matches
 * @returns A setter function to update the context, with an unregister method attached
 */
export function providePageContext(
  urlPattern: string | RegExp,
  initialContext: ChatContextItem[]
): ((context: ChatContextItem[]) => void) & { unregister: () => void } {
  // Always create a new registration to allow multiple providers with the same URL pattern
  const registration: PageContextRegistration = {
    id: generateRegistrationId(),
    urlPattern,
    context: [...initialContext],
  };

  const registry = getPageContextRegistry();
  setPageContextRegistry([...registry, registration]);

  // TODO: Remove this legacy event emission after old event-based approach is fully deprecated
  // Emit legacy update event for backward compatibility
  window.dispatchEvent(
    new CustomEvent(LEGACY_REGISTRY_UPDATE_EVENT, {
      detail: registration,
    })
  );

  // Create setter function that can update the context
  const setContext = (newContext: ChatContextItem[]) => {
    const currentRegistry = getPageContextRegistry();
    const index = currentRegistry.findIndex((reg) => reg.id === registration.id);
    if (index !== -1) {
      const newRegistry = [...currentRegistry];
      newRegistry[index] = { ...newRegistry[index], context: [...newContext] };
      setPageContextRegistry(newRegistry);

      // TODO: Remove this legacy event emission after old event-based approach is fully deprecated
      // Emit legacy update event for backward compatibility
      window.dispatchEvent(
        new CustomEvent(LEGACY_REGISTRY_UPDATE_EVENT, {
          detail: newRegistry[index],
        })
      );
    }
  };

  // Add unregister method to the setter
  setContext.unregister = () => {
    const currentRegistry = getPageContextRegistry();
    const index = currentRegistry.findIndex((reg) => reg.id === registration.id);
    if (index !== -1) {
      const newRegistry = currentRegistry.filter((_, i) => i !== index);
      setPageContextRegistry(newRegistry);

      // TODO: Remove this legacy event emission after old event-based approach is fully deprecated
      // Emit legacy remove event for backward compatibility
      window.dispatchEvent(
        new CustomEvent(LEGACY_REGISTRY_REMOVE_EVENT, {
          detail: { id: registration.id },
        })
      );
    }
  };

  return setContext;
}

/**
 * React hook for providing page context that automatically cleans up on unmount.
 * This is the recommended way to use page context in React components.
 *
 * @param urlPattern - URL pattern (string or RegExp) to match against page URLs
 * @param initialContext - Initial array of ChatContextItem to provide when the pattern matches
 * @returns A setter function to update the context
 */
export function useProvidePageContext(
  urlPattern: string | RegExp,
  initialContext: ChatContextItem[] = []
): (context: ChatContextItem[]) => void {
  const setContextRef = useRef<(((context: ChatContextItem[]) => void) & { unregister: () => void }) | undefined>(
    undefined
  );

  // Store the latest context in a ref to avoid stale closures
  const latestContextRef = useRef(initialContext);
  latestContextRef.current = initialContext;

  // Create or update the registration when dependencies change
  useEffect(() => {
    setContextRef.current = providePageContext(urlPattern, latestContextRef.current);

    // Cleanup on unmount or dependency change
    return () => {
      setContextRef.current?.unregister();
    };
  }, [urlPattern]);

  // Update context when initialContext changes
  useEffect(() => {
    if (setContextRef.current) {
      setContextRef.current(initialContext);
    }
  }, [initialContext]);

  // Return a stable setter function
  const setContext = useCallback((newContext: ChatContextItem[]) => {
    setContextRef.current?.(newContext);
  }, []);

  return setContext;
}

/**
 * Gets all context items that match the current URL.
 *
 * @param currentUrl - The current page URL to match against
 * @param registrations - Array of registrations to match against
 * @returns Array of ChatContextItem from all matching registrations
 */
function getActivePageContext(currentUrl: string): ChatContextItem[] {
  if (!currentUrl) {
    return [];
  }

  const activeContext: ChatContextItem[] = [];
  const registry = getPageContextRegistry();

  for (const registration of registry) {
    if (matchesUrlPattern(currentUrl, registration.urlPattern)) {
      activeContext.push(...registration.context);
    }
  }

  return activeContext;
}

export function usePageContext(
  options: { allowQuestions: boolean; allowComponents: boolean } = { allowQuestions: false, allowComponents: false }
): ChatContextItem[] {
  const [pageContext, setPageContext] = useState<ChatContextItem[]>([]);
  const location = useLocationService();
  const currentPathnameRef = useRef<string>('');

  useEffect(() => {
    // Function to update context based on current location and registry
    const updateContext = () => {
      const pathname = location.getLocation().pathname;
      const context = getActivePageContext(pathname);
      setPageContext(context);
    };

    // Handle registry changes
    const handleRegistryChange = () => {
      updateContext();
    };

    // Subscribe to location changes
    const locationSub = location.getLocationObservable().subscribe((loc: { pathname: string }) => {
      const pathname = loc.pathname;
      if (pathname !== currentPathnameRef.current) {
        currentPathnameRef.current = pathname;
        updateContext();
      }
    });

    // Listen for registry changes
    window.addEventListener(REGISTRY_CHANGE_EVENT, handleRegistryChange);

    // TODO: Remove these legacy event listeners after old event-based approach is fully deprecated
    // Listen for legacy events for backward compatibility
    window.addEventListener(LEGACY_REGISTRY_UPDATE_EVENT, handleRegistryChange);
    window.addEventListener(LEGACY_REGISTRY_REMOVE_EVENT, handleRegistryChange);
    window.addEventListener(LEGACY_REGISTRY_SYNC_EVENT, handleRegistryChange);

    // Initial update
    updateContext();

    return () => {
      locationSub.unsubscribe();
      window.removeEventListener(REGISTRY_CHANGE_EVENT, handleRegistryChange);
      // TODO: Remove these legacy event listener removals after old event-based approach is fully deprecated
      window.removeEventListener(LEGACY_REGISTRY_UPDATE_EVENT, handleRegistryChange);
      window.removeEventListener(LEGACY_REGISTRY_REMOVE_EVENT, handleRegistryChange);
      window.removeEventListener(LEGACY_REGISTRY_SYNC_EVENT, handleRegistryChange);
    };
  }, [location]);

  let filteredContext = pageContext;

  if (!options.allowQuestions) {
    filteredContext = filteredContext.filter((item) => item.node.data?.type !== 'question');
  }

  if (!options.allowComponents) {
    filteredContext = filteredContext.filter((item) => item.node.data?.type !== 'component');
  }

  return filteredContext;
}

export function generateRegistrationId(prefix = 'page-context'): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function matchesUrlPattern(url: string, pattern: string | RegExp): boolean {
  if (pattern instanceof RegExp) {
    return pattern.test(url);
  }

  // For string patterns, support simple wildcards
  if (typeof pattern === 'string') {
    // Convert simple wildcard pattern to regex
    // * matches any characters except /, ** matches any path segments
    // Handle ** first with a placeholder to avoid conflicts
    const regexPattern = pattern
      .replace(/\*\*/g, '\u0000DOUBLE_STAR\u0000') // Temporary placeholder for **
      .replace(/\*/g, '[^/]*') // * becomes [^/]*
      .replace(/\u0000DOUBLE_STAR\u0000/g, '.*') // Replace placeholder with .*
      .replace(/\?/g, '.'); // ? becomes .

    const regex = new RegExp(`^${regexPattern}$`);
    return regex.test(url);
  }

  return false;
}

/**
 * Test utility function to clear all registrations from the registry.
 * This should only be used in tests to ensure a clean slate between test runs.
 *
 * @internal This is for testing purposes only
 */
export function __clearPageContextRegistryForTesting(): void {
  setPageContextRegistry([]);
}
